// 'use client' is load-bearing: this module calls React hooks and is reachable
// from the `@auctogon/ui/web` barrel (same hygiene as useOutsideClick, BL-1124).
'use client';

/**
 * useBackdropDismiss — the one backdrop-dismissal rule for every overlay (BUG-2302,
 * BUG-2308; ADR 044 — Must Consolidate).
 *
 * The browser fires `click` on the common ancestor of the mousedown and mouseup
 * targets, so a press inside a panel that is released over the dimmed layer (a text
 * selection that overshoots, a resize edge, a scrollbar drag) arrives as a click
 * whose target is the layer itself. A plain `e.target === e.currentTarget` check
 * reads that as "clicked outside" and dismisses, losing input or raising a discard
 * prompt. This records where the press began (capture phase, so a child that stops
 * mousedown propagation is still seen) and dismisses only when it began on the layer
 * and travelled no more than the click slop.
 *
 * Spread the returned handlers on the layer that fills the viewport and holds the
 * overlay, or on the standalone backdrop element.
 */

import { useCallback, useRef, type MouseEvent } from 'react';

/** Pointer travel (px) between press and release beyond which a gesture is a drag, not a click. */
export const CLICK_SLOP_PX = 4;

/** Options for {@link useBackdropDismiss}. */
export interface UseBackdropDismissOptions {
    /** When false the returned handlers are undefined, so the layer ignores clicks entirely. */
    enabled?: boolean;
    /** Called for a true click on the layer: the press began there and did not travel. */
    onDismiss: () => void;
}

/** Handlers to spread on the dismissing layer; both are undefined while disabled. */
export interface BackdropDismissHandlers {
    onMouseDownCapture: ((e: MouseEvent<HTMLElement>) => void) | undefined;
    onClick: ((e: MouseEvent<HTMLElement>) => void) | undefined;
}

/**
 * Builds the mousedown-capture and click handlers for a backdrop-dismissing layer.
 *
 * @param options - `onDismiss` plus an optional `enabled` gate (default true).
 * @returns Handlers to spread on the layer; undefined when disabled.
 */
export function useBackdropDismiss({ enabled = true, onDismiss }: UseBackdropDismissOptions): BackdropDismissHandlers {
    const pressRef = useRef<{ target: EventTarget; x: number; y: number } | null>(null);

    const onMouseDownCapture = useCallback((e: MouseEvent<HTMLElement>) => {
        pressRef.current = { target: e.target, x: e.clientX, y: e.clientY };
    }, []);

    const onClick = useCallback(
        (e: MouseEvent<HTMLElement>) => {
            const press = pressRef.current;
            pressRef.current = null;
            if (e.target !== e.currentTarget) return;
            // No recorded press (keyboard or programmatic click) still dismisses.
            if (press && (press.target !== e.currentTarget || Math.hypot(e.clientX - press.x, e.clientY - press.y) > CLICK_SLOP_PX)) return;
            onDismiss();
        },
        [onDismiss],
    );

    return enabled ? { onMouseDownCapture, onClick } : { onMouseDownCapture: undefined, onClick: undefined };
}
